import * as React from 'react';
import { observableValue, autorun } from '@vscode/observables';
import { viewWithModel, ViewModel, prop } from '@vscode/observables-react';
import type { DaemonModel, DaemonScreenshotResult } from '../daemon/index.js';

type ScreenshotState =
	| { readonly status: 'idle' }
	| { readonly status: 'loading' }
	| { readonly status: 'ready'; readonly data: DaemonScreenshotResult }
	| { readonly status: 'error'; readonly message: string };

class FixtureScreenshotModel extends ViewModel({
	fixtureId: prop.const<string>(),
	sessionName: prop.const<string>(),
	daemon: prop.const<DaemonModel>(),
}) {
	readonly screenshot = observableValue<ScreenshotState>(this, { status: 'idle' });
	private _fetchVersion = 0;

	get fixtureId() { return this.props.fixtureId; }

	constructor(props: any) {
		super(props);
		this._store.add(autorun(reader => {
			const sourceTreeIds = this.props.daemon.sourceTreeIds.read(reader);
			const stId = sourceTreeIds.get(this.props.sessionName);
			if (stId) {
				this._fetchScreenshot();
			}
		}));
	}

	private async _fetchScreenshot(): Promise<void> {
		const version = ++this._fetchVersion;
		this.screenshot.set({ status: 'loading' }, undefined);
		try {
			const data = await this.props.daemon.getScreenshot(this.props.sessionName, this.props.fixtureId);
			if (version === this._fetchVersion) {
				this.screenshot.set({ status: 'ready', data }, undefined);
			}
		} catch (e) {
			if (version === this._fetchVersion) {
				this.screenshot.set({ status: 'error', message: String(e) }, undefined);
			}
		}
	}
}

/**
 * Displays a single screenshot from the daemon, with loading/error states.
 * Re-fetches automatically when the session's sourceTreeId changes.
 */
export const FixtureScreenshotView = viewWithModel(
	FixtureScreenshotModel,
	{},
	(reader, model) => {
		const state = model.screenshot.read(reader);

		if (state.status === 'idle' || state.status === 'loading') {
			return (
				<div style={screenshotStyles.placeholder}>
					<div style={screenshotStyles.shimmer} />
				</div>
			);
		}

		if (state.status === 'error') {
			return (
				<div style={screenshotStyles.error}>
					<span style={screenshotStyles.errorText}>Screenshot failed</span>
				</div>
			);
		}

		const { data } = state;
		if (!data.image) {
			return (
				<div style={screenshotStyles.placeholder}>
					<span style={screenshotStyles.hashText}>{data.hash.slice(0, 12)}</span>
				</div>
			);
		}

		return (
			<img
				src={`data:image/png;base64,${data.image}`}
				style={screenshotStyles.image}
				alt={`Screenshot of ${model.fixtureId}`}
			/>
		);
	}
);

const screenshotStyles = {
	placeholder: {
		minHeight: 80,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'var(--component-explorer-editor-background)',
		border: '1px solid var(--component-explorer-panel-border)',
		borderRadius: 4,
		overflow: 'hidden',
	} as React.CSSProperties,
	shimmer: {
		width: '100%',
		height: 80,
		background: 'linear-gradient(90deg, var(--component-explorer-editor-background) 25%, var(--component-explorer-list-hoverBackground) 50%, var(--component-explorer-editor-background) 75%)',
		backgroundSize: '200% 100%',
		animation: 'shimmer 1.5s infinite',
	} as React.CSSProperties,
	error: {
		minHeight: 80,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'var(--component-explorer-inputValidation-errorBackground)',
		border: '1px solid var(--component-explorer-inputValidation-errorBorder)',
		borderRadius: 4,
	} as React.CSSProperties,
	errorText: {
		fontSize: 12,
		color: 'var(--component-explorer-errorForeground)',
	} as React.CSSProperties,
	hashText: {
		fontSize: 11,
		color: 'var(--component-explorer-descriptionForeground)',
		fontFamily: 'var(--component-explorer-editor-font-family, monospace)',
	} as React.CSSProperties,
	image: {
		maxWidth: '100%',
		display: 'block',
		borderRadius: 4,
		border: '1px solid var(--component-explorer-panel-border)',
	} as React.CSSProperties,
};
